import { useCloseDeletedSelection } from "../../components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { IHeader, ILanguage } from "../../../models/Interfaces";
import { HeaderFooterService } from "../../../services/HeaderFooterService";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import Loading from "../../components/common/Loading";
import { Column, CellProps } from 'react-table';
import AddUpdateHeaderModal from "./AddUpdateHeader";
import { ContentService } from "../../../services/ContentService";
import { useConfirm } from "../../../config/ConfirmProvider";
import FilteredTable from "../../components/common/FilteredTable";
import styles from "./headers.module.scss";

interface ISiteHeaders {

}

const SiteHeaders = (props: ISiteHeaders) => {
  const [headers, setHeaders] = useState<Array<IHeader>>([]);
  const [languages, setLanguages] = useState<Array<ILanguage>>([]);
  const [selectedHeader, setSelectedHeader] = useState<IHeader | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);

  const headerSvcRef = useRef<HeaderFooterService>();
  const contentSvcRef = useRef<ContentService>();
  const confirmation = useConfirm();

  const columns: Column<IHeader>[] = useMemo(() => [
    { Header: "Title", accessor: "Name" },
    {
      id: "Language",
      Header: "Language",
      Cell: ({ row }: CellProps<IHeader>) => (
        <>{getLanguage(row.original.Language ?? "")}</>
      )
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IHeader>) => (
        <div className="user-result-actions user-result-actions--single">
          <Button variant="light" onClick={() => editHeader(row.original)} className="user-result-actions__btn user-result-actions__btn--view" title="edit header">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="light" onClick={() => deleteHeader(row.original.Id)} className="user-result-actions__btn user-result-actions__btn--danger" title="Delete header">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [languages, headers]);

  const getLanguages = async (): Promise<void> => {
    if (contentSvcRef.current) {
      const langs = await contentSvcRef.current.getSiteLanguages();
      if (langs) {
        setLanguages(langs);
      }
    }
  };

  const getHeaders = async (): Promise<void> => {
    if (headerSvcRef.current) {
      const headers = await headerSvcRef.current.getSiteHeaders(false);

      if (headers.length > 0) {
        setHeaders(headers);
      }
    }
  };

  const getLanguage = (shortcode: string) => {
    return languages?.find(l => l.ShortCode === shortcode)?.Language ?? shortcode;
  };

  const addHeader = (): void => {
    if (headerSvcRef.current) {
      const newheader = headerSvcRef.current.newSiteHeader();
      if (newheader) {
        setSelectedHeader(newheader);
        setShowModal(true);
      }
    }
  };

  const editHeader = (header: IHeader): void => {
    setSelectedHeader(header);
    setShowModal(true);
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedHeader, (item) => item.Id, () => closeDetails());

  const deleteHeader = async (headerid: string): Promise<void> => {
    if (headerSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation("Delete this header? <b>This action cannot be undone.</b>", "Delete Header");

      if (confirmed) {
        const delres = await headerSvcRef.current.deleteSiteHeader(headerid);

        if (delres) {
          setHeaders((prev) => {
            const filtered = prev.filter(h => h.Id !== headerid);
            return filtered;
          });
          closeDeletedSelection(headerid);
        }
      }
    }
  };

  const saveHeader = async (header: IHeader): Promise<void> => {
    if (headerSvcRef.current) {
      const saved = await headerSvcRef.current.saveSiteHeader(header);

      if (saved) {
        const existing = headers.find(h => h.Id === saved.Id);

        if (existing) {
          const updated = headers.map(h => h.Id === saved.Id ? saved : h);
          setHeaders(updated);
        } else {
          setHeaders(prev => ([
            ...prev,
            saved
          ]));
        }
        closeDetails();
      }
    }
  };

  const closeDetails = (): void => {
    setSelectedHeader(null);
    setShowModal(false);
  };

  useEffect(() => {
    let active = true;
    contentSvcRef.current ??= new ContentService();
    headerSvcRef.current ??= new HeaderFooterService();
    setLoading(true);
    void Promise.allSettled([getLanguages(), getHeaders()]).then(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, []);

  return (
    <>
      {loading && (
        <Row>
          <Col md={12} className="text-center mt-5">
            <Loading Message="loading available site headers..." SpinSize="5x" key={"LoadingSpinner"}></Loading>
          </Col>
        </Row>
      )}

      {!loading && languages?.length > 0 && (
        <div className={styles.page}>
          <section className="app-list-section">
            <div className="app-list-section__header">
              <div>
                <h2 className="app-list-section__title">Header Library</h2>
              </div>
            </div>
            <div className="app-list-section__body">
            <FilteredTable<IHeader>
              columns={columns}
              data={headers}
              noDataMessage="No site headers found, click the add button above to add a new header"
              showAddButton={true}
              addButtonText="Add a site header"
              addButtonIcon={faPlus}
              addButtonHandler={addHeader}
              filterPlaceHolder="filter headers..."
            />
            </div>
          </section>
        </div>
      )}

      {!loading && selectedHeader && showModal && (
        <AddUpdateHeaderModal
          header={selectedHeader}
          onClose={closeDetails}
          onSave={(hdr) => saveHeader(hdr)}
          key={"addUpdateHeaderModal"}
          languages={languages ?? []}
        />
      )}
    </>
  );
};

export default SiteHeaders;
